import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import SEO from '../../components/SEO/SEO';

interface TimeSlot {
  startISO: string;
  endISO: string;
  label: string;
  hour: number;
}

interface DayCell {
  day: number;
  dateStr: string;
  isWeekend: boolean;
  isPast: boolean;
  isDisabled: boolean;
  isSelected: boolean;
  isToday: boolean;
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const EMPLOYEE_OPTIONS = [
  { value: '1-10', label: '1 – 10 employees' },
  { value: '11-50', label: '11 – 50 employees' },
  { value: '51-200', label: '51 – 200 employees' },
  { value: '201-500', label: '201 – 500 employees' },
  { value: '500+', label: '500+ employees' },
];

const ScheduleAppointment: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    employees: '',
    topic: '',
  });

  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [slots, setSlots] = useState<TimeSlot[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [slotsError, setSlotsError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isBooked, setIsBooked] = useState(false);
  const [bookingError, setBookingError] = useState<string | null>(null);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const fetchSlots = useCallback(async (dateStr: string) => {
    setIsLoadingSlots(true);
    setSlotsError(null);
    setSlots([]);
    setSelectedSlot(null);
    try {
      const res = await fetch(`/api/calendar/availability?date=${dateStr}`);
      if (!res.ok) throw new Error('Failed to load slots');
      const data = await res.json();
      setSlots(data.slots || []);
    } catch {
      setSlotsError('Could not load available times. Please try again.');
    } finally {
      setIsLoadingSlots(false);
    }
  }, []);

  useEffect(() => {
    if (selectedDate) {
      fetchSlots(selectedDate);
    }
  }, [selectedDate, fetchSlots]);

  const getDaysInMonth = (): (DayCell | null)[] => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDayOfMonth = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const days: (DayCell | null)[] = [];
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(null);
    }

    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month, d);
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const dayOfWeek = date.getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
      const isPast = date < today;

      days.push({
        day: d,
        dateStr,
        isWeekend,
        isPast,
        isDisabled: isWeekend || isPast,
        isSelected: selectedDate === dateStr,
        isToday: date.getTime() === today.getTime(),
      });
    }
    return days;
  };

  const today = new Date();
  const isPrevMonthDisabled =
    currentMonth.getFullYear() === today.getFullYear() &&
    currentMonth.getMonth() === today.getMonth();

  const prevMonth = () =>
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  const nextMonth = () =>
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));

  const isFormValid =
    formData.name.trim() &&
    formData.email.trim() &&
    formData.company.trim() &&
    formData.employees &&
    selectedSlot;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid || !selectedSlot) return;

    setIsSubmitting(true);
    setBookingError(null);

    try {
      const res = await fetch('/api/calendar/book', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          company: formData.company,
          employees: formData.employees,
          topic: formData.topic,
          startISO: selectedSlot.startISO,
          endISO: selectedSlot.endISO,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Booking failed');
      setIsBooked(true);
    } catch (err: any) {
      setBookingError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatSelectedDate = (dateStr: string) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Success screen
  if (isBooked) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl shadow-xl p-12 max-w-lg w-full text-center"
        >
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Appointment Confirmed!</h2>
          <p className="text-gray-600 mb-2">Your consultation has been scheduled for:</p>
          {selectedDate && selectedSlot && (
            <p className="text-primary-600 font-semibold text-lg mb-6">
              {formatSelectedDate(selectedDate)} at {selectedSlot.label} ET
            </p>
          )}
          <p className="text-gray-500 text-sm mb-8">
            A calendar invitation has been sent to{' '}
            <strong>{formData.email}</strong>. We look forward to speaking with you!
          </p>
          <a
            href="/"
            className="inline-block bg-primary-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors"
          >
            Return to Homepage
          </a>
        </motion.div>
      </div>
    );
  }

  const days = getDaysInMonth();

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Schedule a Consultation — ERHR Global"
        description="Book a free 60-minute HR consultation with ERHR Global. Choose a date and time that works for you."
        url="https://erhrglobal.com/schedule-appointment"
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-primary-600 to-primary-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-4xl lg:text-5xl font-bold mb-6">Schedule a Consultation</h1>
            <p className="text-xl text-primary-100 max-w-3xl mx-auto">
              Book a free 60-minute consultation with our HR experts. Tell us about your
              business and choose a time that works for you.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">

              {/* Left — Form */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                className="bg-white rounded-2xl shadow-xl p-8"
              >
                <h2 className="text-2xl font-bold text-gray-900 mb-1">Your Details</h2>
                <p className="text-gray-500 text-sm mb-6">Tell us a bit about you and your company.</p>

                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Your full name"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="your@company.com"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Company Name *
                    </label>
                    <input
                      type="text"
                      name="company"
                      required
                      value={formData.company}
                      onChange={handleInputChange}
                      placeholder="Your company"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Company Size *
                    </label>
                    <select
                      name="employees"
                      required
                      value={formData.employees}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200"
                    >
                      <option value="">Select company size</option>
                      {EMPLOYEE_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      What would you like to discuss?{' '}
                      <span className="text-gray-400 font-normal">(optional)</span>
                    </label>
                    <textarea
                      name="topic"
                      rows={4}
                      value={formData.topic}
                      onChange={handleInputChange}
                      placeholder="Briefly describe your HR challenge or what you'd like to cover..."
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200 resize-none"
                    />
                  </div>
                </div>
              </motion.div>

              {/* Right — Calendar */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="bg-white rounded-2xl shadow-xl p-8"
              >
                <h2 className="text-2xl font-bold text-gray-900 mb-1">Select Date & Time</h2>
                <p className="text-gray-500 text-sm mb-6">
                  All times are Eastern Time (ET). Consultations are 60 minutes.
                </p>

                {/* Month Navigation */}
                <div className="flex items-center justify-between mb-4">
                  <button
                    type="button"
                    onClick={prevMonth}
                    disabled={isPrevMonthDisabled}
                    className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    aria-label="Previous month"
                  >
                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <span className="text-lg font-semibold text-gray-900">
                    {MONTH_NAMES[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                  </span>
                  <button
                    type="button"
                    onClick={nextMonth}
                    className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                    aria-label="Next month"
                  >
                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>

                {/* Day Headers */}
                <div className="grid grid-cols-7 mb-1">
                  {DAY_NAMES.map(d => (
                    <div
                      key={d}
                      className={`text-center text-xs font-medium py-1 ${
                        d === 'Sun' || d === 'Sat' ? 'text-gray-300' : 'text-gray-400'
                      }`}
                    >
                      {d}
                    </div>
                  ))}
                </div>

                {/* Calendar Grid */}
                <div className="grid grid-cols-7 gap-1 mb-6">
                  {days.map((day, idx) => {
                    if (!day) return <div key={`empty-${idx}`} />;
                    return (
                      <button
                        key={day.dateStr}
                        type="button"
                        disabled={day.isDisabled}
                        onClick={() => setSelectedDate(day.dateStr)}
                        className={[
                          'aspect-square flex items-center justify-center rounded-lg text-sm font-medium transition-all duration-150',
                          day.isDisabled
                            ? 'text-gray-200 cursor-not-allowed'
                            : 'cursor-pointer',
                          day.isSelected
                            ? 'bg-primary-600 text-white'
                            : '',
                          !day.isDisabled && !day.isSelected
                            ? 'text-gray-700 hover:bg-primary-50 hover:text-primary-600'
                            : '',
                          day.isToday && !day.isSelected
                            ? 'ring-2 ring-primary-300 text-primary-600'
                            : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      >
                        {day.day}
                      </button>
                    );
                  })}
                </div>

                {/* Time Slots */}
                {selectedDate ? (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">
                      Available times — {formatSelectedDate(selectedDate)}:
                    </h3>

                    {isLoadingSlots && (
                      <div className="flex items-center justify-center py-8">
                        <svg
                          className="animate-spin h-6 w-6 text-primary-600"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                      </div>
                    )}

                    {!isLoadingSlots && slotsError && (
                      <p className="text-red-500 text-sm py-4">{slotsError}</p>
                    )}

                    {!isLoadingSlots && !slotsError && slots.length === 0 && (
                      <p className="text-gray-500 text-sm py-4">
                        No available times on this date. Please select another day.
                      </p>
                    )}

                    {!isLoadingSlots && !slotsError && slots.length > 0 && (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {slots.map(slot => (
                          <button
                            key={slot.startISO}
                            type="button"
                            onClick={() => setSelectedSlot(slot)}
                            className={[
                              'py-2.5 px-3 rounded-lg text-sm font-medium border transition-all duration-150',
                              selectedSlot?.startISO === slot.startISO
                                ? 'bg-primary-600 text-white border-primary-600'
                                : 'border-gray-200 text-gray-700 hover:border-primary-400 hover:text-primary-600 hover:bg-primary-50',
                            ].join(' ')}
                          >
                            {slot.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center justify-center py-8 text-gray-400">
                    <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span className="text-sm">Select a date to see available times</span>
                  </div>
                )}
              </motion.div>
            </div>

            {/* Submit */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mt-8 text-center"
            >
              {selectedSlot && selectedDate && (
                <p className="text-gray-600 mb-4 text-sm">
                  Selected:{' '}
                  <strong>{formatSelectedDate(selectedDate)}</strong> at{' '}
                  <strong>{selectedSlot.label} ET</strong>
                </p>
              )}

              {bookingError && (
                <p className="text-red-500 text-sm mb-4">{bookingError}</p>
              )}

              <button
                type="submit"
                disabled={!isFormValid || isSubmitting}
                className={[
                  'inline-flex items-center px-12 py-4 rounded-xl font-semibold text-lg transition-all duration-200',
                  isFormValid && !isSubmitting
                    ? 'bg-primary-600 text-white hover:bg-primary-700 hover:scale-105 shadow-lg'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed',
                ].join(' ')}
              >
                {isSubmitting ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Scheduling...
                  </>
                ) : (
                  'Confirm Appointment'
                )}
              </button>

              {!isFormValid && (
                <p className="text-gray-400 text-xs mt-3">
                  {!selectedSlot
                    ? 'Please fill in your details and select a date and time to continue'
                    : 'Please fill in all required fields'}
                </p>
              )}
            </motion.div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default ScheduleAppointment;
